/**
 * A chave do dispositivo (faixa 33 §2, I1): ES256 gerada no navegador, NÃO exportável, guardada num IndexedDB
 * próprio — fora do Dexie do cache e do `clearAllCache` (o logout não a apaga). Ver `chave.md`.
 */
export type EstadoDaChave =
  | { tipo: 'chave'; par: CryptoKeyPair }
  /** Sem `crypto.subtle` (contexto inseguro, http:// na LAN): a sessão segue bearer como hoje. */
  | { tipo: 'sem-suporte' }
  /** Sem IndexedDB, ou ler/gerar/gravar falhou ou passou do teto. Passageira: não fica memoizada (D1). */
  | { tipo: 'falhou' }

export const NOME_DO_BANCO = 'omni-dpop'
export const NOME_DO_STORE = 'chave'
export const NOME_DO_LOCK = 'omni-dpop-chave'
/**
 * Teto próprio (D3): o `timeout` do axios só conta no adapter, DEPOIS dos interceptores, e o trinco `omni-refresh`
 * conta com a seção cuidar do próprio teto (`trinco.ts`). IndexedDB pendurado travaria a renovação de todas as abas.
 */
export const TETO_DA_CHAVE_MS = 3_000

const ID_DO_REGISTRO = 'par'
const ALGORITMO: EcKeyGenParams = { name: 'ECDSA', namedCurve: 'P-256' }
const FALHOU: EstadoDaChave = { tipo: 'falhou' }

/** Só o par obtido e o `sem-suporte` ficam: a falha a próxima chamada tenta de novo. */
let guardada: EstadoDaChave | null = null
let emVoo: Promise<EstadoDaChave> | null = null

export function obterChave(): Promise<EstadoDaChave> {
  if (guardada) return Promise.resolve(guardada)
  if (typeof crypto === 'undefined' || !crypto.subtle) {
    guardada = { tipo: 'sem-suporte' }
    return Promise.resolve(guardada)
  }
  emVoo ??= comTeto(carregar())
    .then((estado) => {
      if (estado.tipo === 'chave') guardada = estado
      return estado
    })
    .finally(() => {
      emVoo = null
    })
  return emVoo
}

function comTeto(trabalho: Promise<CryptoKeyPair>): Promise<EstadoDaChave> {
  let decidido = false
  let relogio: ReturnType<typeof setTimeout> | undefined
  const falhar = (motivo: unknown): EstadoDaChave => {
    if (!decidido) console.warn('Identidade do dispositivo indisponível.', descrever(motivo))
    decidido = true
    return FALHOU
  }
  const teto = new Promise<EstadoDaChave>((resolver) => {
    relogio = setTimeout(() => resolver(falhar('teto')), TETO_DA_CHAVE_MS)
  })
  const obtida = trabalho.then(
    (par): EstadoDaChave => {
      decidido = true
      return { tipo: 'chave', par }
    },
    (erro: unknown) => falhar(erro),
  )
  return Promise.race([obtida, teto]).finally(() => clearTimeout(relogio))
}

/** Pro log: nunca a chave nem o registro — só o nome do erro. */
function descrever(motivo: unknown): string {
  if (motivo === 'teto') return `teto de ${TETO_DA_CHAVE_MS} ms`
  return motivo instanceof Error || motivo instanceof DOMException ? motivo.name : 'erro desconhecido'
}

async function carregar(): Promise<CryptoKeyPair> {
  if (typeof indexedDB === 'undefined' || !indexedDB) throw new Error('IndexedDB ausente')
  const banco = await abrirBanco()
  try {
    const lida = await lerPar(banco)
    if (lida) return lida
    // Duas abas sem chave: a segunda RELÊ dentro do lock e usa a da primeira.
    return await comLock(async () => (await lerPar(banco)) ?? (await gerarEGravar(banco)))
  } finally {
    banco.close()
  }
}

async function gerarEGravar(banco: IDBDatabase): Promise<CryptoKeyPair> {
  const par = await crypto.subtle.generateKey(ALGORITMO, false, ['sign', 'verify'])
  await gravarPar(banco, par)
  return par
}

/** Sem Web Locks (navegador antigo): gera sem lock — a última gravação vence (R38). */
async function comLock(secao: () => Promise<CryptoKeyPair>): Promise<CryptoKeyPair> {
  const locks = typeof navigator === 'undefined' ? undefined : (navigator as { locks?: LockManager }).locks
  if (!locks) return secao()
  // `request` resolve com o que a seção resolve (MDN, LockManager.request()).
  return await locks.request(NOME_DO_LOCK, secao)
}

function abrirBanco(): Promise<IDBDatabase> {
  return new Promise((resolver, rejeitar) => {
    const pedido = indexedDB.open(NOME_DO_BANCO, 1)
    pedido.onupgradeneeded = () => {
      pedido.result.createObjectStore(NOME_DO_STORE)
    }
    pedido.onsuccess = () => resolver(pedido.result)
    pedido.onerror = () => rejeitar(pedido.error ?? new Error('IndexedDB não abriu'))
    pedido.onblocked = () => rejeitar(new Error('IndexedDB bloqueado'))
  })
}

function lerPar(banco: IDBDatabase): Promise<CryptoKeyPair | null> {
  return new Promise((resolver, rejeitar) => {
    const pedido = banco.transaction(NOME_DO_STORE, 'readonly').objectStore(NOME_DO_STORE).get(ID_DO_REGISTRO)
    pedido.onsuccess = () => {
      const valor: unknown = pedido.result
      resolver(ehParDeChaves(valor) ? valor : null)
    }
    pedido.onerror = () => rejeitar(pedido.error ?? new Error('IndexedDB não leu'))
  })
}

function gravarPar(banco: IDBDatabase, par: CryptoKeyPair): Promise<void> {
  return new Promise((resolver, rejeitar) => {
    const tx = banco.transaction(NOME_DO_STORE, 'readwrite')
    tx.objectStore(NOME_DO_STORE).put(par, ID_DO_REGISTRO)
    tx.oncomplete = () => resolver()
    tx.onerror = () => rejeitar(tx.error ?? new Error('IndexedDB não gravou'))
    tx.onabort = () => rejeitar(tx.error ?? new Error('IndexedDB abortou a gravação'))
  })
}

/** Registro fora da forma = chave ausente (D5): gera outra, como se o banco tivesse sido apagado. */
function ehParDeChaves(valor: unknown): valor is CryptoKeyPair {
  if (typeof valor !== 'object' || valor === null) return false
  const { privateKey, publicKey } = valor as Record<string, unknown>
  return ehChaveEcdsa(privateKey, 'private') && ehChaveEcdsa(publicKey, 'public')
}

function ehChaveEcdsa(valor: unknown, tipo: KeyType): boolean {
  if (typeof valor !== 'object' || valor === null) return false
  const chave = valor as Partial<CryptoKey>
  return chave.type === tipo && chave.algorithm?.name === 'ECDSA'
}

/** Só teste: esquece a chave da aba (simula aba nova). O banco fica. */
export function __resetChave(): void {
  guardada = null
  emVoo = null
}
